import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  FileText,
  Clock,
  CheckCircle2,
  TrendingUp,
  XCircle,
  AlertTriangle,
  Wallet,
  ArrowUpRight,
  RefreshCw,
  PlusCircle,
  Building2,
  Percent,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { DatePicker, message } from "antd";
import dayjs from "dayjs";
import "antd/dist/reset.css";
import { useTheme } from "../context/ThemeContext";
import { fintechApi } from "../services/api";

const { RangePicker } = DatePicker;

const PIE_COLORS = ["#2e856e", "#3b82f6", "#f59e0b", "#8b5cf6", "#ec4899", "#10b981", "#6366f1", "#14b8a6"];

const formatINR = (val) => {
  if (val === undefined || val === null || isNaN(val)) return "₹0";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(val);
};

export default function FintechDashboard() {
  const { currentTheme } = useTheme();
  const navigate = useNavigate();
  const primaryColor = currentTheme?.color || "#2e856e";

  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState([
    dayjs().startOf("month"),
    dayjs().endOf("month"),
  ]);

  const [summary, setSummary] = useState({
    totalApplications: 0,
    pending: 0,
    approved: 0,
    disbursed: 0,
    rejected: 0,
  });

  const [portfolio, setPortfolio] = useState({
    totalPortfolio: 0,
    totalOutstanding: 0,
    totalOverdue: 0,
    npaPercentage: 0,
    monthlyTrend: [],
    loanTypeBreakdown: [],
  });

  const [collection, setCollection] = useState({
    efficiencyRate: 0,
    buckets: [
      { bucket: "0-30 Days (Current)", accounts: 0, demand: 0, collected: 0, efficiency: "0%", par: "0%" },
      { bucket: "31-60 Days (Bucket 1)", accounts: 0, demand: 0, collected: 0, efficiency: "0%", par: "0%" },
      { bucket: "61-90 Days (Bucket 2)", accounts: 0, demand: 0, collected: 0, efficiency: "0%", par: "0%" },
      { bucket: "90+ Days (NPA / Default)", accounts: 0, demand: 0, collected: 0, efficiency: "0%", par: "0%" },
    ],
  });

  const fetchDashboardData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (dateRange && dateRange[0] && dateRange[1]) {
        params.startDate = dateRange[0].format("YYYY-MM-DD");
        params.endDate = dateRange[1].format("YYYY-MM-DD");
      }

      const [sumRes, portRes, colRes] = await Promise.allSettled([
        fintechApi.getFintechSummary(params),
        fintechApi.getFintechPortfolio(params),
        fintechApi.getFintechCollection(params),
      ]);

      const failed = [sumRes, portRes, colRes].filter((r) => r.status === "rejected");
      if (failed.length) {
        failed.forEach((r) => console.error("Fintech dashboard section failed:", r.reason));
        message.error(
          failed.length === 3
            ? failed[0].reason?.message || "Failed to load fintech dashboard"
            : "Some dashboard sections failed to load"
        );
      }

      if (sumRes.status === "fulfilled" && sumRes.value) {
        const d = sumRes.value.data || sumRes.value;
        if (d) setSummary((prev) => ({ ...prev, ...d }));
      }
      if (portRes.status === "fulfilled" && portRes.value) {
        const d = portRes.value.data || portRes.value;
        if (d) setPortfolio((prev) => ({ ...prev, ...d }));
      }
      if (colRes.status === "fulfilled" && colRes.value) {
        const d = colRes.value.data || colRes.value;
        if (d) setCollection((prev) => ({ ...prev, ...d }));
      }
    } catch (err) {
      console.error("Fintech Dashboard fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  return (
    <div className="space-y-6 pb-12">
      {/* ── Page Header ── */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-5 rounded-2xl border border-[#e5e7eb] shadow-sm">
        <div>
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center text-white shadow-md"
              style={{ backgroundColor: primaryColor }}
            >
              <Building2 className="w-5 h-5" />
            </div>
            <div>
              <h1 className="text-xl md:text-2xl font-bold text-gray-900">
                Fintech & Lending Dashboard
              </h1>
              <p className="text-xs md:text-sm text-gray-500">
                Real-time loan portfolio, disbursements, and recovery health
              </p>
            </div>
          </div>
        </div>

        {/* Action Controls */}
        <div className="flex flex-wrap items-center gap-3">
          <RangePicker
            value={dateRange}
            onChange={(dates) => setDateRange(dates)}
            format="DD/MM/YYYY"
            className="rounded-xl border-gray-300 py-2 shadow-sm font-medium"
          />

          <button
            onClick={fetchDashboardData}
            disabled={loading}
            className="p-2.5 bg-gray-50 hover:bg-gray-100 text-gray-700 rounded-xl border border-gray-200 transition-all shadow-sm"
            title="Refresh Data"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </button>

          <button
            onClick={() => navigate("/fintech/loans/new")}
            className="flex items-center gap-2 px-4 py-2.5 text-white text-sm font-semibold rounded-xl shadow-md transition-all hover:brightness-105 active:scale-95"
            style={{ backgroundColor: primaryColor }}
          >
            <PlusCircle className="w-4 h-4" />
            <span>New Loan Application</span>
          </button>
        </div>
      </div>

      {/* ── Row 1: Top 5 Stats Row (Application Lifecycle) ── */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {/* Total Applications */}
        <div
          onClick={() => navigate("/fintech/loans")}
          className="bg-white p-5 rounded-2xl border border-gray-200/80 shadow-sm hover:shadow-md transition-all cursor-pointer group"
        >
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-bold uppercase tracking-wider text-gray-500">
              Total Applications
            </span>
            <div className="p-2 bg-blue-50 text-blue-600 rounded-xl group-hover:scale-110 transition-transform">
              <FileText className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl font-black text-gray-900">
            {summary.totalApplications}
          </div>
          <div className="mt-2 text-xs text-blue-600 font-semibold flex items-center gap-1">
            <span>View all files</span>
            <ArrowUpRight className="w-3 h-3" />
          </div>
        </div>

        {/* Pending */}
        <div
          onClick={() => navigate("/fintech/loans?status=SUBMITTED")}
          className="bg-white p-5 rounded-2xl border border-gray-200/80 shadow-sm hover:shadow-md transition-all cursor-pointer group"
        >
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-bold uppercase tracking-wider text-amber-600">
              Under Review
            </span>
            <div className="p-2 bg-amber-50 text-amber-600 rounded-xl group-hover:scale-110 transition-transform">
              <Clock className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl font-black text-gray-900">
            {summary.pending}
          </div>
          <div className="mt-2 text-xs text-amber-600 font-semibold flex items-center gap-1">
            <span>Awaiting credit check</span>
          </div>
        </div>

        {/* Approved */}
        <div
          onClick={() => navigate("/fintech/loans?status=APPROVED")}
          className="bg-white p-5 rounded-2xl border border-gray-200/80 shadow-sm hover:shadow-md transition-all cursor-pointer group"
        >
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-bold uppercase tracking-wider text-emerald-600">
              Sanctioned
            </span>
            <div className="p-2 bg-emerald-50 text-emerald-600 rounded-xl group-hover:scale-110 transition-transform">
              <CheckCircle2 className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl font-black text-gray-900">
            {summary.approved}
          </div>
          <div className="mt-2 text-xs text-emerald-600 font-semibold flex items-center gap-1">
            <span>Ready for payout</span>
          </div>
        </div>

        {/* Disbursed */}
        <div
          onClick={() => navigate("/fintech/loans?status=DISBURSED")}
          className="bg-white p-5 rounded-2xl border border-gray-200/80 shadow-sm hover:shadow-md transition-all cursor-pointer group"
        >
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-bold uppercase tracking-wider text-purple-600">
              Disbursed
            </span>
            <div className="p-2 bg-purple-50 text-purple-600 rounded-xl group-hover:scale-110 transition-transform">
              <TrendingUp className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl font-black text-gray-900">
            {summary.disbursed}
          </div>
          <div className="mt-2 text-xs text-purple-600 font-semibold flex items-center gap-1">
            <span>Active accounts</span>
          </div>
        </div>

        {/* Rejected */}
        <div
          onClick={() => navigate("/fintech/loans?status=REJECTED")}
          className="bg-white p-5 rounded-2xl border border-gray-200/80 shadow-sm hover:shadow-md transition-all cursor-pointer group col-span-2 md:col-span-1"
        >
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-bold uppercase tracking-wider text-red-600">
              Rejected
            </span>
            <div className="p-2 bg-red-50 text-red-600 rounded-xl group-hover:scale-110 transition-transform">
              <XCircle className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl font-black text-gray-900">
            {summary.rejected}
          </div>
          <div className="mt-2 text-xs text-red-600 font-semibold flex items-center gap-1">
            <span>Declined files</span>
          </div>
        </div>
      </div>

      {/* ── Row 2: Portfolio & Financial Health (4 Cards) ── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Portfolio */}
        <div className="bg-gradient-to-br from-white to-emerald-50/30 p-5 rounded-2xl border border-emerald-100 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold uppercase text-gray-500">Total Portfolio</span>
            <div className="p-2 bg-[#2e856e]/10 text-[#2e856e] rounded-xl">
              <Wallet className="w-5 h-5" />
            </div>
          </div>
          <div className="text-2xl font-black text-gray-900">
            {formatINR(portfolio.totalPortfolio)}
          </div>
          <p className="text-xs text-gray-500 mt-1">Total loan volume sanctioned</p>
        </div>

        {/* Total Outstanding */}
        <div className="bg-gradient-to-br from-white to-blue-50/30 p-5 rounded-2xl border border-blue-100 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold uppercase text-gray-500">Total Outstanding</span>
            <div className="p-2 bg-blue-100 text-blue-600 rounded-xl">
              <Building2 className="w-5 h-5" />
            </div>
          </div>
          <div className="text-2xl font-black text-gray-900">
            {formatINR(portfolio.totalOutstanding)}
          </div>
          <p className="text-xs text-gray-500 mt-1">Active principal yet to be recovered</p>
        </div>

        {/* Total Overdue */}
        <div className="bg-gradient-to-br from-white to-amber-50/30 p-5 rounded-2xl border border-amber-100 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold uppercase text-gray-500">Total Overdue</span>
            <div className="p-2 bg-amber-100 text-amber-600 rounded-xl">
              <AlertTriangle className="w-5 h-5" />
            </div>
          </div>
          <div className="text-2xl font-black text-amber-700">
            {formatINR(portfolio.totalOverdue)}
          </div>
          <p className="text-xs text-gray-500 mt-1">Late EMI demand across all buckets</p>
        </div>

        {/* NPA % */}
        <div className="bg-gradient-to-br from-white to-red-50/30 p-5 rounded-2xl border border-red-100 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold uppercase text-gray-500">Gross NPA %</span>
            <div className="p-2 bg-red-100 text-red-600 rounded-xl">
              <Percent className="w-5 h-5" />
            </div>
          </div>
          <div className="text-2xl font-black text-red-700">
            {portfolio.npaPercentage}%
          </div>
          <p className="text-xs text-gray-500 mt-1">Portfolio in default (90+ DPD)</p>
        </div>
      </div>

      {/* ── Row 3: Charts (Disbursement Trend & Portfolio Mix) ── */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left: Monthly Disbursement Bar Chart */}
        <div className="lg:col-span-7 bg-white p-6 rounded-2xl border border-[#e5e7eb] shadow-sm flex flex-col">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-base font-bold text-gray-900">
                Monthly Disbursement vs Target
              </h2>
              <p className="text-xs text-gray-500">
                Monthly performance in INR (₹)
              </p>
            </div>
          </div>

          <div className="h-72 w-full relative">
            {portfolio.monthlyTrend.length === 0 && !loading && (
              <div className="absolute inset-0 flex items-center justify-center text-xs font-semibold text-gray-400 z-10">
                No disbursement data for this period
              </div>
            )}
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={portfolio.monthlyTrend}
                margin={{ top: 10, right: 10, left: 10, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                <XAxis dataKey="month" tick={{ fill: "#6b7280", fontSize: 12 }} />
                <YAxis
                  tickFormatter={(val) => `₹${val / 100000}L`}
                  tick={{ fill: "#6b7280", fontSize: 11 }}
                />
                <Tooltip
                  formatter={(value) => [formatINR(value), ""]}
                  contentStyle={{
                    borderRadius: "12px",
                    border: "1px solid #e5e7eb",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                  }}
                />
                <Legend iconType="circle" />
                <Bar dataKey="target" fill="#cbd5e1" name="Target Amount" radius={[6, 6, 0, 0]} />
                <Bar dataKey="disbursed" fill={primaryColor} name="Disbursed Amount" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Right: Portfolio Distribution Pie Chart */}
        <div className="lg:col-span-5 bg-white p-6 rounded-2xl border border-[#e5e7eb] shadow-sm flex flex-col">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-base font-bold text-gray-900">
                Portfolio by Loan Type
              </h2>
              <p className="text-xs text-gray-500">Asset distribution by volume</p>
            </div>
          </div>

          <div className="h-56 w-full flex items-center justify-center relative">
            {portfolio.loanTypeBreakdown.length === 0 && !loading && (
              <div className="absolute inset-0 flex items-center justify-center text-xs font-semibold text-gray-400 z-10">
                No portfolio data
              </div>
            )}
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={portfolio.loanTypeBreakdown}
                  cx="50%"
                  cy="50%"
                  innerRadius={55}
                  outerRadius={80}
                  paddingAngle={4}
                  dataKey="value"
                >
                  {portfolio.loanTypeBreakdown.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={PIE_COLORS[index % PIE_COLORS.length]}
                    />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => formatINR(value)} />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Custom Legend Table */}
          <div className="mt-2 space-y-1.5 max-h-36 overflow-y-auto pr-1">
            {portfolio.loanTypeBreakdown.map((item, idx) => (
              <div key={idx} className="flex items-center justify-between text-xs py-1 border-b border-gray-50 last:border-0">
                <div className="flex items-center gap-2">
                  <span
                    className="w-2.5 h-2.5 rounded-full"
                    style={{ backgroundColor: PIE_COLORS[idx % PIE_COLORS.length] }}
                  />
                  <span className="font-medium text-gray-700">{item.name}</span>
                  <span className="text-gray-400">({item.count} files)</span>
                </div>
                <span className="font-bold text-gray-900">{formatINR(item.value)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Row 4: Collection Efficiency & Bucket Table ── */}
      <div className="bg-white p-6 rounded-2xl border border-[#e5e7eb] shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
          <div>
            <h2 className="text-base font-bold text-gray-900">
              Collection Efficiency & DPD Aging Matrix
            </h2>
            <p className="text-xs text-gray-500">
              Days Past Due (DPD) demand vs collected amounts
            </p>
          </div>
          <button
            onClick={() => navigate("/fintech/collections")}
            className="text-xs font-bold text-[#2e856e] hover:underline flex items-center gap-1"
          >
            <span>Go to Collections Desk</span>
            <ArrowUpRight className="w-3.5 h-3.5" />
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm border-collapse">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50/75 text-gray-600 text-xs uppercase font-bold">
                <th className="py-3.5 px-4 rounded-l-xl">DPD Bucket</th>
                <th className="py-3.5 px-4 text-center">Active Accounts</th>
                <th className="py-3.5 px-4 text-right">Total Demand</th>
                <th className="py-3.5 px-4 text-right">Total Collected</th>
                <th className="py-3.5 px-4 text-center">Efficiency %</th>
                <th className="py-3.5 px-4 text-right rounded-r-xl">PAR %</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {collection.buckets.map((row, idx) => (
                <tr key={idx} className="hover:bg-gray-50/50 transition-colors">
                  <td className="py-3.5 px-4 font-semibold text-gray-800 flex items-center gap-2">
                    <span
                      className={`w-2.5 h-2.5 rounded-full ${
                        idx === 0
                          ? "bg-emerald-500"
                          : idx === 1
                          ? "bg-amber-500"
                          : idx === 2
                          ? "bg-orange-500"
                          : "bg-red-500"
                      }`}
                    />
                    {row.bucket}
                  </td>
                  <td className="py-3.5 px-4 text-center font-medium text-gray-600">
                    {row.accounts}
                  </td>
                  <td className="py-3.5 px-4 text-right font-semibold text-gray-800">
                    {formatINR(row.demand)}
                  </td>
                  <td className="py-3.5 px-4 text-right font-bold text-emerald-700">
                    {formatINR(row.collected)}
                  </td>
                  <td className="py-3.5 px-4 text-center">
                    <span
                      className={`inline-flex px-2.5 py-1 rounded-full text-xs font-bold ${
                        parseFloat(row.efficiency) > 85
                          ? "bg-emerald-50 text-emerald-700"
                          : parseFloat(row.efficiency) > 50
                          ? "bg-amber-50 text-amber-700"
                          : "bg-red-50 text-red-700"
                      }`}
                    >
                      {row.efficiency}
                    </span>
                  </td>
                  <td className="py-3.5 px-4 text-right font-bold text-gray-700">
                    {row.par}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
